import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, ClockCounterClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

/**
 * Two tenant-side power settings surfaced on the /org page:
 *  1. AI Coach Trust Threshold — slider (50-95) that decides what score
 *     counts as LOW impact (admin only).
 *  2. Manager Digest Preference — per-user snooze / day / hour override.
 * Non-managers can still set prefs so the value is stored if they later
 * become a line manager.
 */
export default function CoachSettingsSection({ isAdmin }) {
  const [threshold, setThreshold] = useState(75);
  const [thresholdInitial, setThresholdInitial] = useState(75);
  const [thresholdBusy, setThresholdBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [prefs, setPrefs] = useState({ disabled: false, skip_next: false, preferred_day: "mon", preferred_hour_utc: 3 });
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [hasEmployee, setHasEmployee] = useState(true);

  const loadHistory = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/tenant/settings/coach-threshold/history?limit=20");
      setHistory(data.items || []);
    } catch (err) {
      // History is a nice-to-have; keep the panel functional but log so
      // devs notice a real failure (auth revoked, endpoint 500, etc.).
      if (process.env.NODE_ENV !== "production") console.warn("[coach-settings] history load failed:", formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenant/settings/coach-threshold");
        setThreshold(data.coach_low_threshold);
        setThresholdInitial(data.coach_low_threshold);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("[coach-settings] threshold load failed:", formatApiError(err.response?.data?.detail) || err.message);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/hr/approver-coach/digest-prefs");
        setPrefs(data);
      } catch (err) {
        if (err.response?.status === 400) setHasEmployee(false);
      }
    })();
    loadHistory();
  }, [isAdmin]);

  const saveThreshold = async () => {
    if (threshold === thresholdInitial) return;
    setThresholdBusy(true);
    try {
      const { data } = await api.put("/tenant/settings/coach-threshold", { coach_low_threshold: Number(threshold) });
      toast.success(`Trust threshold set to ${data.coach_low_threshold}`);
      setThresholdInitial(data.coach_low_threshold);
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save threshold");
    } finally {
      setThresholdBusy(false);
    }
  };

  const savePrefs = async (patch = {}) => {
    setPrefsBusy(true);
    try {
      const merged = { ...prefs, ...patch };
      const { data } = await api.put("/hr/approver-coach/digest-prefs", merged);
      setPrefs(data);
      toast.success("Digest preferences saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save prefs");
    } finally {
      setPrefsBusy(false);
    }
  };

  return (
    <section className="border border-zinc-950 mt-6" data-testid="coach-settings-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
        <Sparkle size={12} weight="fill" className="text-[#FF3E00]" /> coach.settings
      </div>
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trust Threshold */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-bold">AI Coach Trust Threshold</div>
              <div className="text-xs text-zinc-500">Score at/above which a pending leave is labeled <b>LOW impact</b>.</div>
            </div>
            <div className="text-3xl font-black tnum" data-testid="coach-threshold-value">{threshold}</div>
          </div>
          <input
            type="range"
            min={50}
            max={95}
            step={1}
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            disabled={!isAdmin}
            data-testid="coach-threshold-slider"
            className="w-full accent-[#FF3E00] disabled:opacity-40"
          />
          <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
            <span>50 · aggressive</span>
            <span>75 · default</span>
            <span>95 · conservative</span>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-[11px] text-zinc-500">
              {isAdmin
                ? "A higher threshold means fewer leaves qualify for auto-approve."
                : "Admin-only setting. Ask your administrator to change."}
            </div>
            {isAdmin && (
              <button
                onClick={saveThreshold}
                disabled={thresholdBusy || threshold === thresholdInitial}
                data-testid="coach-threshold-save-btn"
                className="font-mono text-[10px] tracking-[0.15em] uppercase bg-zinc-950 text-white px-3 py-1.5 hover:bg-[#FF3E00] disabled:opacity-40"
              >
                {thresholdBusy ? "SAVING…" : "SAVE"}
              </button>
            )}
          </div>
          {isAdmin && history.length > 0 && (
            <div className="mt-4 border-t border-zinc-200 pt-3">
              <button
                onClick={() => setHistoryOpen((v) => !v)}
                data-testid="coach-threshold-history-toggle"
                className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500 hover:text-zinc-950"
              >
                {historyOpen ? "▾" : "▸"} Change history ({history.length})
              </button>
              {historyOpen && (
                <ul className="mt-2 space-y-1 text-[11px] font-mono" data-testid="coach-threshold-history">
                  {history.map((h) => (
                    <li key={h.id} className="flex items-center justify-between text-zinc-600 border-b border-zinc-100 py-1">
                      <span className="truncate">
                        <b className="text-zinc-950">{h.changed_by}</b>
                        {" "}<span className="text-zinc-400">{h.previous} →</span>{" "}
                        <b className="text-[#FF3E00] tnum">{h.new_value}</b>
                      </span>
                      <span className="text-zinc-400 tnum">
                        {h.changed_at ? new Date(h.changed_at).toLocaleString() : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Manager Digest Prefs */}
        <div className={hasEmployee ? "" : "opacity-40 pointer-events-none"}>
          <div className="text-sm font-bold flex items-center gap-2">
            <ClockCounterClockwise size={14} /> Monday Digest Preference
          </div>
          <div className="text-xs text-zinc-500 mb-3">
            The AI Coach digest lands in your inbox every Monday 07:00 (Muscat). Snooze it, disable it, or move it to a different day/hour.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="border border-zinc-300 px-3 py-2 flex items-start gap-2 hover:border-zinc-950 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.disabled}
                onChange={(e) => savePrefs({ disabled: e.target.checked })}
                data-testid="digest-disabled-toggle"
                className="mt-1"
              />
              <div>
                <div className="text-xs font-bold">Turn off digest</div>
                <div className="text-[10px] text-zinc-500">No emails until you re-enable this.</div>
              </div>
            </label>
            <label className="border border-zinc-300 px-3 py-2 flex items-start gap-2 hover:border-zinc-950 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.skip_next}
                onChange={(e) => savePrefs({ skip_next: e.target.checked })}
                data-testid="digest-skip-next-toggle"
                className="mt-1"
              />
              <div>
                <div className="text-xs font-bold">Skip this Monday</div>
                <div className="text-[10px] text-zinc-500">One-off skip. Auto-clears after the next run.</div>
              </div>
            </label>
          </div>
          <div className="mt-4">
            <div className="text-[10px] font-mono tracking-wider text-zinc-500 mb-1.5">DELIVERY DAY (default Mon)</div>
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => savePrefs({ preferred_day: d.key })}
                  data-testid={`digest-day-${d.key}`}
                  disabled={prefs.disabled}
                  className={`font-mono text-[10px] tracking-wider uppercase px-2.5 py-1 border transition-colors ${
                    prefs.preferred_day === d.key
                      ? "bg-zinc-950 text-white border-zinc-950"
                      : "bg-white text-zinc-500 border-zinc-300 hover:border-zinc-950"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <div className="text-[10px] font-mono tracking-wider text-zinc-500 mb-1.5">DELIVERY HOUR (UTC)</div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={23}
                step={1}
                value={prefs.preferred_hour_utc}
                onChange={(e) => savePrefs({ preferred_hour_utc: Number(e.target.value) })}
                disabled={prefs.disabled}
                data-testid="digest-hour-slider"
                className="flex-1 accent-[#FF3E00]"
              />
              <span className="text-sm font-mono tnum w-16 text-right">
                {String(prefs.preferred_hour_utc).padStart(2, "0")}:00
              </span>
            </div>
          </div>
          {prefsBusy && <div className="text-[10px] font-mono text-zinc-400 mt-2">SAVING…</div>}
          {!hasEmployee && (
            <div className="mt-3 text-[10px] font-mono text-zinc-400">
              You need to be linked to an employee record to receive digests.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
